import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home as HomeIcon, LayoutDashboard } from "lucide-react";

import { safeSessionStorage } from '@/lib/browserStorage';
const STORAGE_KEY = "drilllog-tabpath";

// Determine which tab a given pathname belongs to. Only the dashboard and
// its run-sheet children count as the dashboard tab; other screens (tools,
// settings) don't overwrite the dashboard tab's stored path.
function tabFromPath(pathname) {
  if (!pathname) return null;
  if (pathname === "/") return "home";
  if (pathname === "/dashboard" || pathname.startsWith("/run-sheet/")) return "dashboard";
  return null;
}

export default function MobileTabBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const visible = pathname === "/" || pathname === "/dashboard";
  const currentTab = tabFromPath(pathname);

  // Persist the last visited path per tab so switching tabs restores state.
  useEffect(() => {
    try {
      safeSessionStorage.setItem(`${STORAGE_KEY}:${currentTab}`, pathname);
    } catch (e) {
      /* ignore */
    }
  }, [pathname, currentTab]);

  if (!visible) return null;

  const tabs = [
    { key: "home", to: "/", label: "Home", icon: HomeIcon },
    { key: "dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  ];

  const handleClick = (e, tab) => {
    e.preventDefault();
    const root = tab.to;
    if (currentTab === tab.key) {
      // Re-click active tab: reset to its root (or scroll to top if already there).
      if (pathname !== root) {
        navigate(root);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      // Switching tabs: restore that tab's last path, else its root.
      let last = null;
      try {
        last = safeSessionStorage.getItem(`${STORAGE_KEY}:${tab.key}`);
      } catch (err) {
        /* ignore */
      }
      navigate(tabFromPath(last) === tab.key ? last : root);
    }
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 select-none dark:bg-slate-900/95 dark:border-slate-800"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex">
        {tabs.map(({ key, to, label, icon: Icon }) => {
          const active = currentTab === key;
          return (
            <a
              key={key}
              href={to}
              onClick={(e) => handleClick(e, { key, to })}
              className={`flex-1 flex flex-col items-center gap-1 py-2 text-xs font-medium ${
                active ? "text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"
              }`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}